import styled from "@emotion/styled"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import ReactPlayer from "react-player"
import { Container, CustomBreadcrumbs, NewText, transitionTime } from "ui"
import VideoPauseIcon from "ui/icons/video/VideoPauseIcon"
import VideoPlayIcon from "ui/icons/video/VideoPlayIcon"
import { transitionEase } from "ui/transitions/utils"
import { extractProperty, getUmbracoMediaUrl } from "utils"

const StyledOuter = styled.div``

const StyledButton = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  cursor: pointer;
  transition: opacity ${transitionTime}s ${transitionEase};

  &:hover {
    opacity: 0.8;
  }
`

// Wrapper that handles hover-zoom for both images and videos
const MediaContainer = styled.div`
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  height: 184px;
  width: 304px;

  img,
  video {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    transition: transform ${transitionTime}s ${transitionEase};
  }

  &:hover img,
  &:hover video {
    transform: scale(1.3);
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 487px;
    height: 260px;
  }
`

const MediaOrder = styled.div``

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  position: relative;
  margin: 32px 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 88px;
    margin-top: 88px;
  }
`

const StyledContainer = styled.div<{ $inView: boolean; $isReversed?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  position: relative;
  opacity: ${(props) => (props.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  flex-direction: column-reverse;
  gap: 24px;
  padding-inline-start: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-inline-start: 0;
    opacity: ${(props) => (props.$inView ? 1 : 0)};
    display: grid;
    grid-template-columns: 1fr 1fr;
    ${MediaOrder} {
      order: ${(props) => (props.$isReversed ? 1 : 0)};
      justify-self: ${(props) => (props.$isReversed ? "flex-start" : "flex-end")};
    }
    gap: 64px;
    flex-direction: ${(props) => (props.$isReversed ? "row-reverse" : "row")};
  }
`

const TextBox = styled.div<{ $isReversed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-left: ${({ $isReversed }) => ($isReversed ? "0" : "40px")};
    padding-right: ${({ $isReversed }) => ($isReversed ? "40px" : "0")};
    text-align: ${(p) => (p.$isReversed ? "right" : "left")};
    justify-self: ${(props) => (props.$isReversed ? "flex-end" : "flex-start")};
  }
`

// Simple styled Image that just fills its parent
const StyledImage = styled(Image)`
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
`

const TimelineLine = styled.div<{ $height?: number }>`
  position: absolute !important;
  width: 2px;
  height: ${({ $height }) => ($height ? `${$height}px` : "100%")};
  left: 0;
  top: 18px;
  background-image: url("/images/verticalLine.png");
  background-repeat: repeat-y;
  background-size: 2px 720px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    top: 20px;
    background-image: url("/images/verticalLine.png");
    left: 50%;
    transform: translateX(-50%);
  }
`

const StyledPin = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 200px;
  z-index: 1;
  box-sizing: content-box;

  border: 3px solid var(--White, #fff);
  background: var(---, #89c744);
  box-shadow: 0px 0px 16px 0px rgba(166, 207, 81, 0.8);
  position: absolute;
  left: -7px;
  top: 13px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    top: 20px;
    left: 50%;
    transform: translateX(-50%); // Center the pin horizontally
  }
`

const MobileBusWrapper = styled.div<{ $top: number; $visible: boolean }>`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
  }
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  transition: top 0.4s ${transitionEase}, opacity 0.2s ${transitionEase};
  top: ${({ $top }) => `${$top}px`};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  z-index: 2;
`

const StyledNewText = styled(NewText)`
  p {
    margin: 0 !important;
    text-align: inherit !important;
  }
`

function Video({ url }: { url: string }) {
  const [isPlaying, setIsPlaying] = useState(true)

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying)
  }

  return (
    <>
      <StyledButton onClick={togglePlayPause}>
        {isPlaying ? <VideoPauseIcon /> : <VideoPlayIcon />}
      </StyledButton>
      <ReactPlayer
        url={getUmbracoMediaUrl(url)}
        width={487}
        height={260}
        playing={isPlaying}
        loop={true}
        muted={true}
        playsinline={true}
        controls={false}
      />
    </>
  )
}
export default function ConcreteProcessStep({
  data,
  selfObject,
  parents,
}: {
  data: any
  selfObject: any
  parents: any
}) {
  const concreteProcessSteps = extractProperty(data, "concreteProcessSteps") || []
  const steps = concreteProcessSteps.map((step: any) => {
    return {
      title: extractProperty(step, "title"),
      text: extractProperty(step, "text"),
      stepName: extractProperty(step, "stepName"),
      media: extractProperty(step, "media"),
    }
  }) as {
    title: string
    text: string
    stepName: string
    media: any
  }[]
  const layoutRef = useRef<HTMLDivElement | null>(null)
  const BUS_HEIGHT = 180 // height of bus image in px

  // refs to each container so we can reveal them when bus passes
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])

  // visible state for each step (revealed by bus)
  const [visibleSteps, setVisibleSteps] = useState<boolean[]>([])

  // bus position and visibility state
  const [busTop, setBusTop] = useState(0)
  const [busVisible, setBusVisible] = useState(false)
  const [timelineHeight, setTimelineHeight] = useState<number | undefined>(undefined)

  // keep previous busTop in a ref for synchronous access in scroll handler
  const busTopRef = useRef(0)

  // refs to dynamically size the timeline line
  const lineRef = useRef<HTMLDivElement | null>(null)
  const lastPinRef = useRef<HTMLDivElement | null>(null)

  // scroll handler to update bus position relative to viewport
  useEffect(() => {
    const handleScroll = () => {
      if (!layoutRef.current) return
      const layoutTop = layoutRef.current.offsetTop
      const layoutHeight = layoutRef.current.offsetHeight

      const { scrollY, innerHeight: viewportHeight } = window

      // calculate desired top inside layout (candidate position)
      let newTop = scrollY + viewportHeight - layoutTop - BUS_HEIGHT

      // clamp within layout bounds, but stop at the last step position
      let maxTop = layoutHeight
      if (stepRefs.current[steps.length - 1]) {
        // Get the last step container position relative to the layout
        const lastStepContainer = stepRefs.current[steps.length - 1]
        if (lastStepContainer) {
          const lastStepTop = lastStepContainer.offsetTop
          maxTop = Math.min(lastStepTop, layoutHeight)
        }
      }

      newTop = Math.max(0, Math.min(newTop, maxTop))

      // allow movement in both directions (up and down)
      if (newTop !== busTopRef.current) {
        busTopRef.current = newTop
        setBusTop(newTop)
      }

      const inTimelineVertically =
        scrollY + viewportHeight > layoutTop && scrollY < layoutTop + layoutHeight
      // Keep bus visible throughout the timeline, including at the bottom
      setBusVisible(inTimelineVertically)

      // compute bus bottom relative to layout (0 = top of layout)
      const busBottom = newTop + BUS_HEIGHT

      // reveal/hide steps based on bus position (bidirectional)
      setVisibleSteps((prev) => {
        const next = [...prev]
        stepRefs.current.forEach((stepEl, idx) => {
          if (!stepEl) return
          // Show step if bus has passed its top
          if (busBottom >= stepEl.offsetTop) {
            next[idx] = true
          } else {
            // Hide step if bus has moved back above it
            next[idx] = false
          }
        })
        return next
      })
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    // initial call
    handleScroll()

    return () => window.removeEventListener("scroll", handleScroll)
  }, [steps.length])

  // initialize visibleSteps length when steps change
  useEffect(() => {
    setVisibleSteps(new Array(steps.length).fill(false))
  }, [steps.length])

  // calculate timeline height to end at the center of the last pin
  useEffect(() => {
    const updateTimelineHeight = () => {
      if (lastPinRef.current && layoutRef.current) {
        // Get the position of the last pin relative to the layout container
        const layoutRect = layoutRef.current.getBoundingClientRect()
        const pinRect = lastPinRef.current.getBoundingClientRect()

        // Calculate the relative position within the layout
        const pinTopRelativeToLayout =
          pinRect.top - layoutRect.top + lastPinRef.current.offsetHeight / 2

        // Account for the timeline starting point (20px on desktop)
        const timelineStartOffset = window.innerWidth >= 768 ? 20 : 0
        const calculatedHeight = pinTopRelativeToLayout - timelineStartOffset

        setTimelineHeight(Math.max(calculatedHeight, 0))
      }
    }

    // Delay the calculation to ensure all elements are rendered
    const timeoutId = setTimeout(updateTimelineHeight, 200)

    // Also listen for resize events
    window.addEventListener("resize", updateTimelineHeight)
    window.addEventListener("scroll", updateTimelineHeight)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener("resize", updateTimelineHeight)
      window.removeEventListener("scroll", updateTimelineHeight)
    }
  }, [visibleSteps, steps.length])

  return (
    <StyledOuter>
      <Container>
        <CustomBreadcrumbs current={selfObject} parents={parents} />

        <StyledLayout ref={layoutRef}>
          <TimelineLine ref={lineRef} $height={timelineHeight} />
          <MobileBusWrapper $top={busTop} $visible={busVisible}>
            <Image
              src="/images/bus.png"
              alt="History Timeline Bus"
              style={{ objectFit: "contain" }}
              width={48}
              height={180}
            />
          </MobileBusWrapper>
          {steps.map((el, index) => (
            <StyledContainer
              ref={(node) => {
                stepRefs.current[index] = node
              }}
              $inView={visibleSteps[index]}
              $isReversed={index % 2 === 0}
              key={index}
            >
              <MediaOrder>
                {el.media && (
                  <MediaContainer>
                    {el.media.includes("mp4") ? (
                      <Video url={getUmbracoMediaUrl(el.media)} />
                    ) : (
                      <StyledImage
                        src={getUmbracoMediaUrl(el.media)}
                        alt={el.title}
                        width={487}
                        height={260}
                      />
                    )}
                  </MediaContainer>
                )}
              </MediaOrder>
              <StyledPin ref={index === steps.length - 1 ? lastPinRef : undefined} />
              <TextBox $isReversed={index % 2 === 0}>
                <NewText
                  color={"#537A26"}
                  desktopFontWeight={700}
                  desktopFontSize={32}
                  desktopLineHeight={"normal"}
                  mobileFontSize={24}
                  component={"h2"}
                  style={{ marginBottom: "-8px" }}
                >
                  {el.stepName}
                </NewText>
                <NewText
                  component={"h3"}
                  desktopFontSize={24}
                  mobileFontSize={20}
                  desktopFontWeight={700}
                  desktopLineHeight={"100%"}
                  color="black"
                >
                  {el.title}
                </NewText>
                <StyledNewText
                  component={"p"}
                  desktopFontSize={16}
                  desktopLineHeight={"100%"}
                  color="black"
                  dangerouslySetInnerHTML={{ __html: el.text }}
                />
              </TextBox>
            </StyledContainer>
          ))}
        </StyledLayout>
      </Container>
    </StyledOuter>
  )
}
